import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import BuySearchAreaMap from './BuySearchAreaMap';
import { drawableAreas, type BuySearchMapArea } from './buySearchAreas';
import { KIND_WORDS } from './leaseSearch';

/**
 * THE BUYING TAB (BUY1): the off-market buildings that fit a buyer's search, drawn from the contract's `pipeline`
 * (`kind: "buy-search"`) and `buyBox`. Top to bottom: the heading, date and notice with the counts; his search; the area
 * map; the best fits; the other fits; and, folded away, the buildings that have left the search.
 *
 * ⛔ THE PAGE NEVER COMPUTES A FIGURE. The counts, the area counts, the dates and every word on a card are the contract's
 * own (the import gate re-counts the tallies). The one thing derived here is the "Show the other 9 best fits (6 in ...)"
 * button, and it is counted from the very cards it hides, so it cannot name something that is not behind it.
 *
 * ⛔ NOTHING HERE CAN LEAK A BUILDING. The map gets the list of areas and nothing else; a card is drawn by the page's own
 * `renderCard` from the building the contract sent, and a buy search that holds its addresses back sends none. No code in
 * this file asks which buildings are likely to sell, has a loan, or is "best" except by the contract's own groups.
 *
 * The contract types are MIRRORED here, not imported: a frontend file never imports backend source (it typechecks locally
 * and fails the Vercel build - 2026-09-09). Every field is optional except a building's `id`, so the page's own deal and
 * group types fit these structurally and its `renderDealCard` is a valid `renderCard`.
 */

export interface BuySearchNotice {
  text?: string;
  phone?: string;
  phoneLabel?: string;
}

export interface BuySearchGroup {
  label?: string;
  description?: string;
  mapColor?: string;
  /** Display order. jsonb does not keep key order, so the legend is sorted by this, never by arrival. */
  order?: number;
  /** The one group that leads the tab, above the other fits. */
  lead?: boolean;
  /** A group of buildings that have left the search: folded away, and in no count. */
  closing?: boolean;
}

export interface BuySearchDeal {
  id: string;
  name?: string;
  /** The area name while addresses are held back; the real city once they are shared. */
  city?: string;
  /** The key of the area the building is counted in. Only read when a card carries no city. */
  area?: string;
  status?: { state?: string; group?: string; label?: string };
}

export interface BuySearchPipeline {
  asOf?: string;
  intro?: string;
  notice?: BuySearchNotice;
  counts?: { fit?: number; byGroup?: Record<string, number> };
  areas?: BuySearchMapArea[];
  groups?: Record<string, BuySearchGroup>;
  deals?: BuySearchDeal[];
}

/** The buy box: what the buyer asked for. Only `rows` is read; anything else on it is ignored. */
export type BuySearchBuyBox = { rows?: unknown } | null | undefined;

export interface BuySearchTabProps {
  pipeline: BuySearchPipeline;
  buyBox: BuySearchBuyBox;
  /** The page's own card (`ClientPortal`'s `renderDealCard`). The tab hands it the building and its group. */
  renderCard: (deal: BuySearchDeal, group: BuySearchGroup | undefined) => ReactNode;
  /** The page's note thread for a section, mounted under that section's heading (the page owns who may write). */
  sectionNotes: (key: string) => ReactNode;
}

const WORDS = KIND_WORDS['buy-search'];

/** Fixed by the spec (design 4.1); not in the contract. */
const HEADING = 'Buildings that fit your search';
const NAV_BUYING = 'Buildings that fit';
const NAV_MAP = 'Map';

/** How many cards of a group show before the rest sit behind the button. */
export const VISIBLE_CARDS = 3;

const OTHER_KEY = '__other';

const isRecord = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === 'object' && !Array.isArray(v);
const isSentence = (v: unknown): v is string => typeof v === 'string' && v.trim().length > 0;

const humanize = (key: string): string => key.replace(/[-_]+/g, ' ').trim().replace(/^./, (c) => c.toUpperCase());

/* ───────────────────────────────── the date ───────────────────────────────── */

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/**
 * "Updated October 28, 2026" from "2026-10-28", cut from the digits as written. `new Date('2026-10-28')` is midnight UTC,
 * which is the evening of the 27th on a machine west of Greenwich; this never builds a local Date at all. A string that is
 * not a real calendar date gives null, and the page then prints no date line rather than a guess.
 */
export const updatedText = (asOf: unknown): string | null => {
  const m = typeof asOf === 'string' ? /^(\d{4})-(\d{2})-(\d{2})/.exec(asOf) : null;
  if (!m) return null;
  const [year, month, day] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const check = new Date(Date.UTC(year, month - 1, day));
  if (check.getUTCFullYear() !== year || check.getUTCMonth() !== month - 1 || check.getUTCDate() !== day) return null;
  return `Updated ${MONTHS[month - 1]} ${day}, ${year}`;
};

/* ───────────────────────────────── the groups ───────────────────────────────── */

interface Bucket {
  key: string;
  group: BuySearchGroup | undefined;
  label: string;
  rows: BuySearchDeal[];
}

interface Layout {
  /** The best fits, above the map's neighbours. Null when no group is flagged `lead`, or it has no cards. */
  lead: Bucket | null;
  /** Every other group that is still in the search, in the legend order, then a trailing "Other" for a stray building. */
  open: Bucket[];
  /** The groups flagged `closing`: buildings that have left the search. */
  closed: Bucket[];
}

/** Group keys in the contract's `order`. Groups without one keep their arrival position after the ordered ones. */
const orderedKeys = (groups: Record<string, BuySearchGroup>): string[] => {
  const rank = (k: string): number => (typeof groups[k]?.order === 'number' ? (groups[k].order as number) : Number.MAX_SAFE_INTEGER);
  return Object.keys(groups).sort((a, b) => rank(a) - rank(b));
};

/**
 * Where every building goes. Cards keep the contract's order inside a group (best fits first, then by area, is the
 * builder's call and is never re-sorted here). A building whose group the legend does not name still shows, under
 * "Other" - it never vanishes.
 */
const layoutOf = (pipeline: BuySearchPipeline): Layout => {
  const groups = pipeline.groups ?? {};
  const deals = pipeline.deals ?? [];
  const keys = orderedKeys(groups);
  const buckets: Bucket[] = keys
    .map((key) => ({ key, group: groups[key], label: groups[key]?.label || humanize(key), rows: deals.filter((d) => d.status?.group === key) }))
    .filter((b) => b.rows.length > 0);
  const strays = deals.filter((d) => !d.status?.group || !keys.includes(d.status.group));
  const lead = buckets.find((b) => b.group?.lead === true && b.group?.closing !== true) ?? null;
  const closed = buckets.filter((b) => b !== lead && b.group?.closing === true);
  const open = buckets.filter((b) => b !== lead && b.group?.closing !== true);
  if (strays.length > 0) open.push({ key: OTHER_KEY, group: undefined, label: 'Other', rows: strays });
  return { lead, open, closed };
};

/* ───────────────────────────────── the buy box ───────────────────────────────── */

interface SearchRow {
  label: string;
  value: string;
}

const searchRowsOf = (buyBox: BuySearchBuyBox): SearchRow[] => {
  const rows = isRecord(buyBox) ? buyBox.rows : null;
  if (!Array.isArray(rows)) return [];
  return rows.filter((r): r is SearchRow => isRecord(r) && isSentence(r.label) && isSentence(r.value));
};

/** A sentence-length value reads left-aligned; a figure keeps the right edge (the same rule the page's fact lists use). */
const LONG_FACT = 60;
const factClass = (value: string): string => (value.length > LONG_FACT ? 'portal-fact portal-fact-long' : 'portal-fact');

/* ───────────────────────────────── the menu ───────────────────────────────── */

/**
 * The jump-menu and print-chooser entries for this tab, in page order. Built from the SAME conditions that draw the
 * sections, so a chip never jumps to nothing. Pass the buy box to drop "Your search" when it has no rows; left out, the
 * search is listed (a buy-search contract always carries its rows).
 */
export function buySearchNavEntries(pipeline: BuySearchPipeline, buyBox?: BuySearchBuyBox): Array<{ key: string; label: string }> {
  const layout = layoutOf(pipeline);
  const entries: Array<{ key: string; label: string }> = [{ key: 'buying', label: NAV_BUYING }];
  if (buyBox === undefined || searchRowsOf(buyBox).length > 0) entries.push({ key: 'criteria', label: WORDS.criteria });
  if (drawableAreas(pipeline.areas).length > 0) entries.push({ key: 'areas', label: NAV_MAP });
  if (layout.lead) entries.push({ key: 'lead', label: layout.lead.label });
  if (layout.open.length + layout.closed.length > 0) entries.push({ key: 'deals', label: WORDS.deals });
  return entries;
}

/* ───────────────────────────────── the button's words ───────────────────────────────── */

/**
 * What the button calls the cards it hides (the ruling, 2026-10-28): "the other 9 best fits" in the lead group, and "the
 * other 8 that fit" in every other group - never a noun cut from a group's label ("the other 8 also fits").
 */
const nounOf = (isLead: boolean, count: number): string => {
  if (isLead) return count === 1 ? 'best fit' : 'best fits';
  return count === 1 ? 'that fits' : 'that fit';
};

/**
 * "6 in Northeast Riverside, 2 in San Bernardino", busiest first (equal counts by name), counted from the hidden cards
 * themselves. A card with no city falls back to the area it is counted in; one with neither is left out of the list.
 */
const areaBreakdown = (rows: BuySearchDeal[], areaLabels: Map<string, string>): string => {
  const counts = new Map<string, number>();
  for (const d of rows) {
    const name = (isSentence(d.city) ? d.city.trim() : '') || (d.area ? areaLabels.get(d.area) ?? '' : '');
    if (name) counts.set(name, (counts.get(name) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([name, n]) => `${n} in ${name}`)
    .join(', ');
};

/* ───────────────────────────────── the hash ───────────────────────────────── */

const DEAL_HASH = '#deal-';

/** "#deal-building-13" -> "building-13"; anything else -> null. */
const dealIdOfHash = (hash: string): string | null => {
  if (!hash.startsWith(DEAL_HASH)) return null;
  try {
    return decodeURIComponent(hash.slice(DEAL_HASH.length));
  } catch {
    return null;
  }
};

/* ───────────────────────────────── the component ───────────────────────────────── */

export default function BuySearchTab({ pipeline, buyBox, renderCard, sectionNotes }: BuySearchTabProps) {
  const layout = useMemo(() => layoutOf(pipeline), [pipeline]);
  const areaLabels = useMemo(() => new Map((pipeline.areas ?? []).map((a) => [a.key, a.label])), [pipeline.areas]);
  const searchRows = searchRowsOf(buyBox);

  /* Which groups have their hidden cards showing (and which closing groups are open), by group key. */
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const setGroupOpen = (key: string, value: boolean) =>
    setExpanded((prev) => (Boolean(prev[key]) === value ? prev : { ...prev, [key]: value }));

  /*
    A link to a building that sits behind the button or in the folded group ("#deal-building-13") must still arrive: the
    group opens, then the card is scrolled to. The page switches to this tab for such a link (portalTabs.tabForHash); this
    is the half that only the tab knows - which cards are hidden. Every card is in the page, so the target exists.
  */
  const homes = useMemo(() => {
    const m = new Map<string, string>();
    for (const b of layout.lead ? [layout.lead, ...layout.open] : layout.open) b.rows.slice(VISIBLE_CARDS).forEach((d) => m.set(d.id, b.key));
    for (const b of layout.closed) b.rows.forEach((d) => m.set(d.id, b.key));
    return m;
  }, [layout]);
  const knownIds = useMemo(() => new Set((pipeline.deals ?? []).map((d) => d.id)), [pipeline.deals]);
  const [jump, setJump] = useState<string | null>(null);

  useEffect(() => {
    const follow = () => {
      const id = dealIdOfHash(window.location.hash);
      if (id === null || !knownIds.has(id)) return;
      const groupKey = homes.get(id);
      if (groupKey !== undefined) setGroupOpen(groupKey, true);
      setJump(id);
    };
    follow();
    window.addEventListener('hashchange', follow);
    return () => window.removeEventListener('hashchange', follow);
  }, [homes, knownIds]);

  useEffect(() => {
    if (jump === null) return;
    document.getElementById(`deal-${jump}`)?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    setJump(null);
  }, [jump]);

  const cards = (rows: BuySearchDeal[], group: BuySearchGroup | undefined): ReactNode =>
    rows.map((d) => <Fragment key={d.id}>{renderCard(d, group)}</Fragment>);

  /* The first three cards, then (when there are more) a button that names the rest, then the rest. */
  const groupCards = (bucket: Bucket, listClass: string): ReactNode => {
    const shown = bucket.rows.slice(0, VISIBLE_CARDS);
    const rest = bucket.rows.slice(VISIBLE_CARDS);
    const isOpen = Boolean(expanded[bucket.key]);
    const moreId = `buy-more-${bucket.key}`;
    const noun = nounOf(bucket === layout.lead, rest.length);
    const breakdown = areaBreakdown(rest, areaLabels);
    return (
      <>
        <div className={listClass}>{cards(shown, bucket.group)}</div>
        {rest.length > 0 && (
          <>
            <button
              type="button"
              className="portal-buy-more-button"
              aria-expanded={isOpen}
              aria-controls={moreId}
              onClick={() => setGroupOpen(bucket.key, !isOpen)}
            >
              {isOpen
                ? `Hide the other ${rest.length} ${noun}`
                : `Show the other ${rest.length} ${noun}${breakdown ? ` (${breakdown})` : ''}`}
            </button>
            <div id={moreId} className={`${listClass} portal-buy-more`} hidden={!isOpen}>
              {cards(rest, bucket.group)}
            </div>
          </>
        )}
      </>
    );
  };

  const count = (bucket: Bucket) => <span className="portal-group-count">({bucket.rows.length})</span>;

  /* ── the heading, the notice and the counts ── */
  const updated = updatedText(pipeline.asOf);
  const notice = pipeline.notice;
  const phone = isSentence(notice?.phone) ? notice.phone.trim() : '';
  const phoneDigits = phone.replace(/\D/g, '');
  const callWords = `${isSentence(notice?.phoneLabel) ? notice.phoneLabel.trim() : 'Call'} at ${phone}`;
  const tiles: Array<{ key: string; label: string; n: number }> = [];
  if (typeof pipeline.counts?.fit === 'number') tiles.push({ key: 'fit', label: 'buildings fit', n: pipeline.counts.fit });
  for (const key of orderedKeys(pipeline.groups ?? {})) {
    const group = pipeline.groups?.[key];
    const n = pipeline.counts?.byGroup?.[key];
    // A building that has left the search is in no count: a closing group never gets a tile, whatever a contract says.
    if (group?.closing !== true && typeof n === 'number') tiles.push({ key, label: group?.label || humanize(key), n });
  }

  return (
    <>
      <section className="portal-section" id="section-buying">
        <h2>{HEADING}</h2>
        {sectionNotes('buying')}
        {updated && <p className="portal-subtitle portal-buy-updated">{updated}</p>}
        {isSentence(pipeline.intro) && <p className="portal-headline portal-buy-intro">{pipeline.intro}</p>}
        {(isSentence(notice?.text) || phone) && (
          <div className="portal-callout portal-buy-notice">
            {isSentence(notice?.text) && <p className="portal-callout-sub">{notice.text}</p>}
            {phone && (
              <p className="portal-callout-sub portal-buy-call">
                {phoneDigits.length >= 10 ? <a href={`tel:${phoneDigits}`}>{callWords}</a> : callWords}
              </p>
            )}
          </div>
        )}
        {tiles.length > 0 && (
          <dl className="portal-stats portal-buy-stats">
            {tiles.map((t) => (
              <div className="portal-stat" key={t.key}>
                <dt>{t.label}</dt>
                <dd>{t.n}</dd>
              </div>
            ))}
          </dl>
        )}
      </section>

      {searchRows.length > 0 && (
        <section className="portal-section" id="section-criteria">
          <h2>{WORDS.criteria}</h2>
          {sectionNotes('criteria')}
          <div className="portal-fact-block">
            <dl className="portal-facts">
              {searchRows.map((r) => (
                <div className={factClass(r.value)} key={r.label}>
                  <dt>{r.label}</dt>
                  <dd>{r.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>
      )}

      {drawableAreas(pipeline.areas).length > 0 && (
        <section className="portal-section" id="section-areas">
          <h2>{NAV_MAP}</h2>
          {sectionNotes('areas')}
          {/* The map is handed the contract's areas whole and draws the ones that have a building in them. */}
          <BuySearchAreaMap areas={pipeline.areas ?? []} />
          <p className="portal-note">Each bubble is an area, with the number of buildings that fit there.</p>
        </section>
      )}

      {layout.lead && (
        <section className="portal-section" id="section-lead">
          <h2>
            {layout.lead.label} {count(layout.lead)}
          </h2>
          {sectionNotes('lead')}
          {layout.lead.group?.description && <p className="portal-group-desc">{layout.lead.group.description}</p>}
          {groupCards(layout.lead, 'portal-deal-list portal-lead-list')}
        </section>
      )}

      {layout.open.length + layout.closed.length > 0 && (
        <section className="portal-section" id="section-deals">
          <h2>{WORDS.deals}</h2>
          {sectionNotes('deals')}
          {layout.open.map((b) => (
            <div className="portal-group" key={b.key} style={{ borderLeftColor: b.group?.mapColor || 'var(--apex-line)' }}>
              <h3>
                {b.label} {count(b)}
              </h3>
              {b.group?.description && <p className="portal-group-desc">{b.group.description}</p>}
              {groupCards(b, 'portal-deal-list')}
            </div>
          ))}
          {layout.closed.map((b) => (
            <details
              className="portal-group portal-buy-closed"
              key={b.key}
              open={Boolean(expanded[b.key])}
              onToggle={(e) => setGroupOpen(b.key, e.currentTarget.open)}
              style={{ borderLeftColor: b.group?.mapColor || 'var(--apex-line)' }}
            >
              <summary className="portal-group-summary">
                <h3>
                  {b.label} {count(b)}
                </h3>
              </summary>
              <div className="portal-deal-list">{cards(b.rows, b.group)}</div>
            </details>
          ))}
        </section>
      )}
    </>
  );
}
